import React, { useState, useEffect, useMemo } from 'react';
import SEO from '../components/SEO';
import BlogPostImage from '../components/BlogPostImage';
import { normalizePost } from '../utils/wordpress';
import '../styles/blogPage.css';

const WP_API_BASE = process.env.REACT_APP_WP_API_BASE || 'https://public-api.wordpress.com/wp/v2/sites/qashqgmail.wordpress.com';
const SITE_BASE = process.env.REACT_APP_SITE_BASE || 'https://qashqgmail.wordpress.com/';

function getSlidesToShow(width) {
  if (width < 768) return 1;
  if (width < 992) return 2;
  return 3;
}

function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(() =>
    typeof window !== 'undefined' ? getSlidesToShow(window.innerWidth) : 1
  );

  useEffect(() => {
    const handleResize = () => {
      const next = getSlidesToShow(window.innerWidth);
      setSlidesToShow(next);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Fetch posts from WordPress REST API
    const fetchBlogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${WP_API_BASE}/posts?per_page=9&_embed`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch blogs');
        }

        const data = await response.json();
        setBlogs(data || []);
        setCurrentIndex(0);
      } catch (err) {
        setError(err.message);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const normalizedBlogs = useMemo(
    () => blogs.map((blog) => normalizePost(blog, SITE_BASE)),
    [blogs],
  );

  const maxIndex = Math.max(0, normalizedBlogs.length - slidesToShow);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const totalSlides = Math.max(1, Math.ceil(normalizedBlogs.length / slidesToShow));
  const itemsToDisplay = Math.min(normalizedBlogs.length, slidesToShow);

  return (
    <>
      <SEO
        title="Blog"
        description="Insights, tips, and updates on business consulting, accounting, IT solutions, and digital transformation from QASHQ Business & IT Consultants."
        path="/blog"
      />
      <main className="blog-page">
      {/* Header Section */}
      <section className="page-hero blog-page-header">
        <div className="container">
          <div className="page-hero-inner reveal-on-scroll">
            <p className="section-eyebrow">Blog</p>
            <h1 className="page-title">Insights &amp; Updates</h1>
            <p className="page-subtitle">
              Insights, tips, and updates on business, accounting, and IT solutions
            </p>
          </div>
        </div>
      </section>

      {/* Blog Carousel Section */}
      <section className="blog-carousel-section">
        <div className="container">
          <h2 className="blog-section-heading">Latest Articles</h2>

          {error && (
            <div className="blog-error-message">
              <p>Note: To display WordPress blogs, please make sure your WordPress site is properly configured.</p>
              <p>Update the .env file with your WordPress API URL.</p>
            </div>
          )}

          {loading ? (
            <div className="blog-loading">
              <p>Loading blog posts...</p>
            </div>
          ) : normalizedBlogs.length > 0 ? (
            <>
              <div className="blog-carousel-wrapper">
                <button 
                  className="carousel-btn prev-btn" 
                  onClick={handlePrev}
                  style={{ visibility: normalizedBlogs.length <= slidesToShow ? 'hidden' : 'visible' }}
                  aria-label="Previous"
                >
                  <ion-icon name="chevron-back-outline"></ion-icon>
                </button>

                <div className="blog-carousel-container">
                  <div 
                    className="blog-carousel-track"
                    style={{
                      display: 'flex',
                      gap: slidesToShow === 1 ? '0px' : '20px',
                      transform: `translateX(-${currentIndex * (100 / slidesToShow)}%)`,
                      transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      width: `${Math.max(100, (normalizedBlogs.length / slidesToShow) * 100)}%`,
                      justifyContent: itemsToDisplay < slidesToShow ? 'center' : 'flex-start',
                    }}
                  >
                    {normalizedBlogs.map((blog) => (
                      <article
                        key={blog.id}
                        className="blog-carousel-card"
                        style={{
                          flex: slidesToShow === 1 ? '0 0 100%' : `0 0 calc(${100 / slidesToShow}% - ${(slidesToShow - 1) * 20 / slidesToShow}px)`,
                          minWidth: slidesToShow === 1 ? '100%' : `calc(${100 / slidesToShow}% - ${(slidesToShow - 1) * 20 / slidesToShow}px)`,
                          maxWidth: slidesToShow === 1 ? '100%' : 'none',
                        }}
                      >
                        <div className="blog-image">
                          <BlogPostImage src={blog.image} alt={blog.title} />
                        </div>

                        <div className="blog-content">
                          <p className="blog-date">
                            <ion-icon name="calendar-outline"></ion-icon>
                            {blog.date}
                          </p>

                          <h3 className="blog-card-title">{blog.title}</h3>

                          <p className="blog-excerpt">{blog.excerpt}</p>

                          <a href={blog.link} className="blog-read-more">
                            Read More
                            <ion-icon name="arrow-forward-outline"></ion-icon>
                          </a>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>

                <button className="carousel-btn next-btn" onClick={handleNext} aria-label="Next" style={{ visibility: normalizedBlogs.length <= slidesToShow ? 'hidden' : 'visible' }}>
                  <ion-icon name="chevron-forward-outline"></ion-icon>
                </button>
              </div>

              {normalizedBlogs.length > slidesToShow && (
                <div className="blog-carousel-dots">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${currentIndex === index ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    ></button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="blog-empty">
              <p>No blog posts available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="blog-newsletter-section">
        <div className="container">
          <h2 className="newsletter-heading">Stay Updated</h2>
          <p className="newsletter-text">
            Subscribe to our newsletter for the latest insights and updates on business and IT solutions.
          </p>

          <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="Your email address"
              required
              className="newsletter-input"
            />
            <button type="submit" className="newsletter-button">
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </main>
    </>
  );
}

export default BlogPage;
